import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HorizontalStepper, LabelAndValue } from '@medtronic/pecc-react-component-library-js';
import { Alert, Box, Grid, Modal, Stack } from '@mui/material';
import { DatasetCreationEnum } from '~/api/types';
import { useGetDatasetEnumsQuery, useGetDatasetQuery } from '~/api/v2/hooks';
import { ModalHeader } from '~/components';
import { modalContainerStyle, modalOverflowBoxStyles } from '~/components/styles';
import { ImportDataIcon, ImportSupportiveDocumentsIcon } from '~/icons';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { ImportDataStep, ModalActionBlock, SupportiveDocumentsStep } from '.';

export enum ImportDataStepsEnum {
  SUPPORTING_DOCUMENTS = 0,
  DATA_IMPORT = 1,
}

export interface ImportDataModalProps {
  payload: {
    id: string;
    onFilesUpload: (supportiveDocuments: File[] | null, dataFiles: File[] | null) => void;
  };
}

const ImportDataModal: FC<ImportDataModalProps> = ({ payload: { id, onFilesUpload } }) => {
  const datasetId = +useParams().id!;
  const dispatch = useAppDispatch();
  const { data } = useGetDatasetQuery({ datasetId });
  const { data: locationsData } = useGetDatasetEnumsQuery({ enumKey: DatasetCreationEnum.GEO_LOCATION });
  const [activeStep, setActiveStep] = useState<ImportDataStepsEnum>(ImportDataStepsEnum.SUPPORTING_DOCUMENTS);
  const [dataFiles, setDataFiles] = useState<File[] | null>(null);
  const [supportiveDocuments, setSupportiveDocuments] = useState<File[] | null>(null);

  useEffect(() => {
    if (!data?.supportive_docs_upload_allowed && data?.source_docs_upload_allowed) {
      setActiveStep(ImportDataStepsEnum.DATA_IMPORT);
    }
  }, [data, open]);

  const dataConfig = [
    {
      label: 'Dataset Name',
      value: data?.name,
    },
    {
      label: 'Dataset Id',
      value: data?.id,
    },
    {
      label: 'Dataset Status',
      value: data?.status,
    },
    {
      label: 'Region',
      value: !data?.geo_location_tags
        ? '—'
        : locationsData
            ?.filter(({ key }) => data?.geo_location_tags?.includes(key))
            .map(({ value }) => value.replace('GeoLocation_', ''))
            .join(', '),
    },
    {
      label: 'Ou Owner',
      value: data?.source_ou,
    },
  ];

  const handleClose = () => {
    dispatch(closeModalById(id));
    setActiveStep(ImportDataStepsEnum.SUPPORTING_DOCUMENTS);
    setDataFiles(null);
    setSupportiveDocuments(null);
  };

  const handleSubmit = () => {
    if (activeStep === ImportDataStepsEnum.SUPPORTING_DOCUMENTS) {
      setActiveStep(ImportDataStepsEnum.DATA_IMPORT);
    } else {
      onFilesUpload(supportiveDocuments, dataFiles);
      handleClose();
    }
  };

  const uploadedFiles = activeStep === ImportDataStepsEnum.SUPPORTING_DOCUMENTS ? supportiveDocuments : dataFiles;

  return (
    <Modal open onClose={handleClose}>
      <Box sx={modalContainerStyle}>
        <ModalHeader
          onClose={handleClose}
          title={[
            'Import data to dataset',
            'Please complete the following screens to bulk import data into a dataset.',
          ]}
        />
        <Grid container px={1}>
          {dataConfig.map(({ label, value }) => (
            <Grid size={{ md: 12 / 5, sm: 3 }}>
              <LabelAndValue label={label} key={label} value={value || '—'} />
            </Grid>
          ))}
        </Grid>
        {!!uploadedFiles?.length && (
          <Grid size={12}>
            <Alert sx={{ mx: 1 }} severity="success">
              {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} added and ready to upload
            </Alert>
          </Grid>
        )}
        <Stack sx={{ ...modalOverflowBoxStyles, p: 0, height: '55vh' }}>
          <Box>
            <HorizontalStepper
              activeStepNumber={activeStep}
              stepsConfig={[
                {
                  stepLabel: 'Import Supportive Documents',
                  stepIcon: <ImportSupportiveDocumentsIcon />,
                  hideStepButton: true,
                },
                {
                  stepLabel: 'Import Data',
                  stepIcon: <ImportDataIcon />,
                  hideStepButton: true,
                },
              ]}
            />
          </Box>
          {activeStep === ImportDataStepsEnum.SUPPORTING_DOCUMENTS && (
            <SupportiveDocumentsStep
              files={supportiveDocuments || []}
              onFilesUpload={(files) => setSupportiveDocuments(files)}
            />
          )}
          {activeStep === ImportDataStepsEnum.DATA_IMPORT && (
            <ImportDataStep files={dataFiles || []} onFilesUpload={(files) => setDataFiles(files)} />
          )}
        </Stack>
        <ModalActionBlock
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          onSubmit={handleSubmit}
          onClose={handleClose}
          submitDisabled={
            activeStep === ImportDataStepsEnum.DATA_IMPORT && !supportiveDocuments?.length && !dataFiles?.length
          }
        />
      </Box>
    </Modal>
  );
};

export default ImportDataModal;
